import * as React from 'react';
import {FC, useEffect, useMemo, useState} from 'react';
import {useNotify} from 'react-admin';
import {Box, Chip, IconButton, ListItem, ListItemText, TextField, ListItemAvatar, Avatar} from '@mui/material';
import {Add} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import {debounce} from '@mui/material/utils';

import {addToList, fetchSearch} from './api/api';
import type {ShortWineDto} from '../../types/wine';

interface WineAutocompleteProps {
  onAdded: (w: ShortWineDto) => void;
}
export const WineAutocomplete:FC<WineAutocompleteProps> = ({
                            onAdded}) =>  {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ShortWineDto[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useMemo(
    () =>
      debounce(async (q: string) => {
        if (!q || q.trim().length < 2) {
          setOptions([]);
          return;
        }
        setLoading(true);
        try {
          const res = await fetchSearch(q);
          setOptions(res ?? []);
        } catch (e: any) {
          notify(e?.message || 'Search failed', { type: 'warning' });
        } finally {
          setLoading(false);
        }
      }, 350),
    [notify]
  );

  useEffect(() => {
    void load(input);
    return () => load.clear?.();
  }, [input, load]);

  const handleSelect = async (_: any, value: ShortWineDto | null) => {
    if (!value) return;
    try {
      await addToList(value.id);
      notify(`Added: ${value.name}`, { type: 'info' });
      onAdded(value);
      setInput('');
      setOptions([]);
    } catch (e: any) {
      notify(e?.message || 'Failed to add', { type: 'error' });
    }
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      loading={loading}
      options={options}
      getOptionLabel={(o) => o.name}
      filterOptions={(x) => x} // server-side filtering
      value={null}
      onChange={handleSelect}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      blurOnSelect
      sx={{ minWidth: 480 }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Add a wine…"
          placeholder='Try: "Malbec", "KARIA 2023", "Brunello 2017"'
        />
      )}
      renderOption={(props, o) => (
        <ListItem {...props} key={o.id} secondaryAction={
          <IconButton
            edge="end"
            aria-label="add"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelect(null, o)}
          >
            <Add fontSize="small" />
          </IconButton>
        }>

          <ListItemAvatar>
            <Avatar
              alt={o.name}
              src={o.imageUrl ?? '/wine-placeholder.jpg'}
              variant="square"
              sx={{ width: 56, height: 56, mr: 1 }}
            />
          </ListItemAvatar>

          <ListItemText
            primary={o.name}
            secondary={
              <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .5 }}>
                {o.producer && <Chip size="small" label={o.producer} />}
                {o.vintage && <Chip size="small" label={o.vintage} />}
                {o.type && <Chip size="small" label={o.type} />}
                {o.region && <Chip size="small" label={o.region} />}
                {o.country && <Chip size="small" label={o.country} />}
              </Box>
            }
          />
        </ListItem>
      )}
    />
  );
}
